/**
 * Prompt de instalación PWA para el Promotor Digital.
 *
 * Android/Chrome: captura `beforeinstallprompt` → botón "Instalar app" que
 * dispara el prompt nativo (1 toque).
 * iPhone/Safari: Apple no permite prompt automático → mostramos una mini-guía
 * ("Compartir → Agregar a inicio").
 *
 * El botón aparece en el header del panel. Si ya está instalada (standalone)
 * o no hay forma de instalar, no se muestra.
 */

import { t } from './languages.js';
import { esStandalone, esIOS, esAndroid } from './systems/deteccion-entorno.js';

let _deferredPrompt = null;

function estaInstalada() {
    return esStandalone();
}

export function initInstallPrompt() {
    window.addEventListener('beforeinstallprompt', e => {
        // Evitar el mini-infobar de Chrome; lo disparamos nosotros desde el botón.
        e.preventDefault();
        _deferredPrompt = e;
    });
    window.addEventListener('appinstalled', () => {
        _deferredPrompt = null;
    });
}

// ¿Hay que mostrar el botón de instalar?
function debeMostrarBoton() {
    if (estaInstalada()) return false;
    // Android: tenemos el prompt diferido. iOS: siempre (guía manual).
    return !!_deferredPrompt || esIOS();
}

export function renderBotonInstalar() {
    if (!debeMostrarBoton()) return '';
    return `<button class="btn btn-install" id="pm-install"
        style="width:auto; padding:8px 12px; font-size:13px;">${t('instalar.boton')}</button>`;
}

// Guard anti-doble-ejecución: prompt() es de un solo uso por evento; un doble-tap
// del nini impaciente sobre el botón estrella tiraría error en la 2da llamada.
let _promptEnProgreso = false;

// Dispara la instalación. Devuelve true si lanzó el PROMPT NATIVO (Android/Chromium);
// false si NO hay prompt (iOS, in-app, navegador sin soporte) → el caller debe
// mostrar la guía contextual. NUNCA llamar fuera de un gesto de usuario.
export async function manejarClickInstalar() {
    if (!_deferredPrompt) return false;
    if (_promptEnProgreso) return true; // ya en curso: no re-disparar
    _promptEnProgreso = true;
    try {
        _deferredPrompt.prompt();
        await _deferredPrompt.userChoice; // {outcome:'accepted'|'dismissed'} — no celebramos acá; 'appinstalled' confirma
    } catch (_) {
        /* el usuario canceló o el prompt ya no era válido */
    } finally {
        // El deferred es de un solo uso; si fue 'dismissed' Chrome reenviará un
        // beforeinstallprompt nuevo en otra navegación y _deferredPrompt se re-arma.
        _deferredPrompt = null;
        _promptEnProgreso = false;
    }
    return true;
}

// Muestra la guía de instalación adecuada al entorno (cuando NO hay prompt nativo).
// `codigo` (el código de acceso) se muestra COPIABLE arriba de la guía iOS: en iOS
// la app instalada no recibe el código solo, el promotor debe copiarlo ANTES de
// instalar y pegarlo en la app vacía. Reusa el overlay dorado.
export function mostrarGuiaInstalacion(camino, codigo) {
    if (camino === 'in-app') return mostrarGuiaInApp();
    if (camino === 'ios-otro') return mostrarGuiaAbrirEnSafari();
    // ios-safari y cualquier otro fallback → la guía Compartir → Agregar a inicio.
    return mostrarGuiaIOS(codigo);
}

// Ícono "Compartir" de iOS (cuadrado con flecha hacia arriba) — el que el usuario
// debe tocar en Safari. Reconocible para que sepa exactamente dónde tocar.
function iconCompartirIOS() {
    return '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v13"/><path d="m7 8 5-5 5 5"/><path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7"/></svg>';
}

// Ícono "Agregar a inicio" de iOS (cuadrado con +) — el que el usuario elige en el
// menú Compartir. Igual al de la captura, para que sepa exactamente cuál tocar.
function iconAgregarInicioIOS() {
    return '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="4"/><path d="M12 8v8"/><path d="M8 12h8"/></svg>';
}

// Copia `texto` al portapapeles con los 3 fallbacks iOS-safe (patrón PIX): Clipboard
// API → execCommand síncrono → selección manual. Refleja el resultado en el botón.
function _copiarCodigoIOS(texto, boton, textoOk, textoManual) {
    function reflejar(ok) {
        // ok → "✓ Copiado" (queda fijo, no revierte: el promotor ve que SÍ se copió).
        // !ok → instrucción de copiar a mano (selecciona el input para el menú iOS).
        boton.textContent = ok ? textoOk : textoManual;
        if (ok) boton.classList.add('pm-ios-codigo-copiado');
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(texto).then(
            () => reflejar(true),
            () => reflejar(_copiarConExecCommand(texto))
        );
    } else {
        reflejar(_copiarConExecCommand(texto));
    }
}

// Fallback execCommand iOS-safe (textarea 1px visible, font 16px, setSelectionRange).
function _copiarConExecCommand(texto) {
    const ta = document.createElement('textarea');
    ta.value = texto;
    ta.setAttribute('readonly', '');
    ta.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;padding:0;border:none;font-size:16px';
    document.body.appendChild(ta);
    let ok = false;
    try {
        ta.focus();
        ta.select();
        ta.setSelectionRange(0, texto.length);
        ok = document.execCommand('copy');
    } catch (_) {
        ok = false;
    }
    document.body.removeChild(ta);
    return ok;
}

// Monta un overlay dorado con contenido HTML y lo cierra al tocar fuera o el botón
// con id 'pm-ios-ok'. SSOT del overlay de las guías de instalación.
function _montarOverlayGuia(innerHTML) {
    const overlay = document.createElement('div');
    overlay.className = 'pm-ios-install-overlay';
    overlay.innerHTML = `<div class="pm-ios-install-modal">${innerHTML}</div>`;
    overlay.addEventListener('click', e => {
        if (e.target === overlay || e.target.id === 'pm-ios-ok') overlay.remove();
    });
    document.body.appendChild(overlay);
    return overlay;
}

function mostrarGuiaIOS(codigo) {
    // PASO 0 — copiar el código (lo más importante en iOS): la app instalada arranca
    // vacía y el promotor debe pegar su código ahí. Cajita seleccionable + botón
    // copiar. Solo se muestra si tenemos el código.
    const bloqueCodigo = codigo
        ? `
        <div class="pm-ios-codigo-bloque">
            <div class="pm-ios-codigo-label">${t('bienvenida.iosCopiaPrimero')}</div>
            <div class="pm-ios-codigo-row">
                <input class="pm-ios-codigo-input" id="pm-ios-codigo" type="text" readonly value="${codigo}" />
                <button class="pm-ios-codigo-copiar pm-flash-dorado" id="pm-ios-copiar">${t('bienvenida.codigoCopiar')}</button>
            </div>
        </div>`
        : '';

    const overlay = _montarOverlayGuia(`
        <div class="pm-ios-install-emoji">📲</div>
        <h2 class="pm-ios-install-titulo">${t('bienvenida.iosTitulo')}</h2>
        ${bloqueCodigo}
        <div class="pm-ios-pasos">
            <div class="pm-ios-paso">
                <span class="pm-ios-paso-num">1</span>
                <span class="pm-ios-paso-txt">${t('bienvenida.iosPaso1')}</span>
                <span class="pm-ios-paso-icon">${iconCompartirIOS()}</span>
            </div>
            <div class="pm-ios-paso">
                <span class="pm-ios-paso-num">2</span>
                <span class="pm-ios-paso-txt">${t('bienvenida.iosPaso2')}</span>
                <span class="pm-ios-paso-icon">${iconAgregarInicioIOS()}</span>
            </div>
            <div class="pm-ios-paso">
                <span class="pm-ios-paso-num pm-ios-paso-num-ok">✓</span>
                <span class="pm-ios-paso-txt">${t('bienvenida.iosPaso3')}</span>
            </div>
        </div>
        <button class="btn btn-install" id="pm-ios-ok">${t('bienvenida.iosOk')}</button>`);

    const btnCopiar = overlay.querySelector('#pm-ios-copiar');
    const inpCodigo = overlay.querySelector('#pm-ios-codigo');
    if (btnCopiar && codigo) {
        const txtOk = '✓ ' + t('bienvenida.codigoCopiado');
        btnCopiar.addEventListener('click', () => {
            _copiarCodigoIOS(codigo, btnCopiar, txtOk, t('bienvenida.iosCopiaManual'));
            btnCopiar.classList.remove('pm-flash-dorado'); // ya copió: dejar de flashear
            if (inpCodigo) { inpCodigo.focus(); inpCodigo.select(); }
        });
    }
    // Tocar el número lo SELECCIONA entero → el promotor puede copiar a mano con el
    // menú de iOS (readonly a veces no autoselecciona al tocar). Cubre el caso de que
    // el botón Copiar falle por permisos de iOS.
    if (inpCodigo) {
        inpCodigo.addEventListener('click', () => { inpCodigo.focus(); inpCodigo.select(); });
        inpCodigo.addEventListener('focus', () => inpCodigo.select());
    }
}

// Promotor en Chrome/Firefox iOS (no Safari): solo Safari ofrece "Agregar a inicio".
function mostrarGuiaAbrirEnSafari() {
    _montarOverlayGuia(`
        <div class="pm-ios-install-emoji">🧭</div>
        <h2 class="pm-ios-install-titulo">${t('bienvenida.iosOtroTitulo')}</h2>
        <p class="pm-ios-install-texto">${t('bienvenida.iosOtroTexto')}</p>
        <button class="btn btn-install" id="pm-ios-ok">${t('bienvenida.iosOk')}</button>`);
}

// Promotor dentro del navegador interno de WhatsApp/Instagram/etc.: no se puede
// instalar acá. En Android intentamos abrir Chrome con un intent (best-effort);
// siempre acompañado de la instrucción manual (el intent puede fallar callado).
function mostrarGuiaInApp() {
    const overlay = _montarOverlayGuia(`
        <div class="pm-ios-install-emoji">🌐</div>
        <h2 class="pm-ios-install-titulo">${t('bienvenida.inAppTitulo')}</h2>
        <p class="pm-ios-install-texto">${t('bienvenida.inAppTexto')}</p>
        <button class="btn btn-install" id="pm-inapp-abrir">${t('bienvenida.inAppBoton')}</button>
        <button class="pm-bienvenida-web" id="pm-ios-ok">${t('bienvenida.iosOk')}</button>`);
    const btn = overlay.querySelector('#pm-inapp-abrir');
    if (btn) {
        btn.addEventListener('click', () => {
            // Android: intent:// para abrir en Chrome con fallback a la URL https.
            if (esAndroid()) {
                const url = window.location.href;
                const sinEsquema = url.replace(/^https?:\/\//, '');
                window.location.href =
                    `intent://${sinEsquema}#Intent;scheme=https;package=com.android.chrome;` +
                    `S.browser_fallback_url=${encodeURIComponent(url)};end`;
            }
            // iOS in-app no tiene un esquema fiable para forzar Safari → la
            // instrucción manual del texto es la vía. No cerramos el overlay.
        });
    }
}
